import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { TablePaginationConfig } from 'antd/lib/table';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import { DEFAULT_PAGINATION } from '../../../domain/commonConst';
import { usePageLocation } from '../../../pages/usePageState';
import { ICustomerUtilityBill } from 'src/domain/customer-utility-bill/interface';
import { ICustomerUtilityBillsQuery } from 'src/domain/customer-utility-bill';
import { IBillSidebarFilters, getBillPeriodRange } from 'src/components/customer-utility-bill/sidebar/billSidebarFilters';
import { SORT_FIELD_BY_KEY } from './billColumns';

export interface IPaginationState {
    current: number;
    pageSize: number;
}

export interface ISorterState {
    field?: string;
    order?: 'ascend' | 'descend';
}

const DEFAULT_SORTER: ISorterState = { field: 'updatedAt', order: 'descend' };

interface IBillPageState {
    filter?: Partial<Record<keyof IBillSidebarFilters, unknown>>;
    search?: string;
    pagination?: { current?: number; pageSize?: number };
    sorter?: ISorterState;
}

const toArr = (v: unknown): string[] => (v == null ? [] : Array.isArray(v) ? v.map(String) : [String(v)]);

/**
 * Owns the Customer Utility Bills page state. Filters / search / pagination / sorter live in the URL so
 * they survive a reload; everything derived from `location.search` is memoized on that raw string so the
 * `query` object (and therefore the react-query key) only changes when the URL actually changes.
 */
export const useCustomerUtilityBillPageState = () => {
    const { search: locationSearch } = useLocation();
    const { setPageQuery, queryToState } = usePageLocation();

    // `queryToState` is a pure parser of the URL string, so memoizing on the raw search is correct.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const pageState = useMemo(() => queryToState(locationSearch) as IBillPageState, [locationSearch]);

    const filters = useMemo<IBillSidebarFilters>(
        () => ({
            period: (pageState.filter?.period as string | null) ?? null,
            statuses: toArr(pageState.filter?.statuses) as IBillSidebarFilters['statuses'],
            verification: (pageState.filter?.verification as IBillSidebarFilters['verification']) ?? null,
            utilities: toArr(pageState.filter?.utilities),
        }),
        [pageState]
    );

    const search = pageState.search ?? '';

    const pagination = useMemo<IPaginationState>(
        () => ({
            current: pageState.pagination?.current ?? 1,
            pageSize: pageState.pagination?.pageSize ?? DEFAULT_PAGINATION.pageSize,
        }),
        [pageState]
    );

    // Mirrors the repository's default ordering (see CustomerUtilityBillRepository.buildOrderBy) so the
    // "Updated At" column shows an active sort arrow even before the user has clicked a column header.
    const sorter = useMemo<ISorterState>(() => pageState.sorter ?? DEFAULT_SORTER, [pageState]);

    const query = useMemo<ICustomerUtilityBillsQuery>(() => {
        const periodRange = getBillPeriodRange(filters.period);
        const verified = filters.verification === 'verified' ? 'true' : filters.verification === 'unverified' ? 'false' : undefined;

        return {
            ...(search && { search }),
            ...(filters.statuses.length && { statuses: filters.statuses.join(',') }),
            ...(verified && { verified }),
            ...(filters.utilities.length && { utilities: filters.utilities.join(',') }),
            ...(periodRange?.start && { periodStart: periodRange.start }),
            ...(periodRange?.end && { periodEnd: periodRange.end }),
            ...(sorter.field && sorter.order && { sortField: sorter.field, sortOrder: sorter.order }),
            offset: (pagination.current - 1) * pagination.pageSize,
            limit: pagination.pageSize,
        };
    }, [search, filters, sorter, pagination]);

    const setFilters = useCallback(
        // reset to page 1 whenever filters change
        (next: IBillSidebarFilters) => setPageQuery({ ...pageState, filter: next, pagination: { current: 1, pageSize: pagination.pageSize } }),
        [pageState, pagination.pageSize, setPageQuery]
    );

    const clearFilters = useCallback(
        // drop all sidebar filters from the URL and go back to page 1 (search/sort are kept)
        () => setPageQuery({ ...pageState, filter: undefined, pagination: { current: 1, pageSize: pagination.pageSize } }),
        [pageState, pagination.pageSize, setPageQuery]
    );

    // usePageLocation resets pagination to page 1 automatically when search changes
    const setSearch = useCallback((value: string) => setPageQuery({ ...pageState, search: value }), [pageState, setPageQuery]);

    const onTableChange = useCallback(
        (
            tablePagination: TablePaginationConfig,
            _filter: Record<string, FilterValue | null>,
            tableSorter: SorterResult<ICustomerUtilityBill> | SorterResult<ICustomerUtilityBill>[]
        ) => {
            const nextSorter = Array.isArray(tableSorter) ? tableSorter[0] : tableSorter;
            const rawField = (nextSorter?.field ?? nextSorter?.columnKey) as string | undefined;
            // antd wipes field/columnKey when a controlled sort gets cancelled (3rd click: ascend -> descend
            // -> cancelled). Only one column is ever active, so that unambiguously means "cancel the current
            // field" — fall back to it instead of trusting the blanked event.
            const cancelledField = sorter.field ?? DEFAULT_SORTER.field;
            const newSorter: ISorterState | undefined = rawField
                ? { field: SORT_FIELD_BY_KEY[rawField] ?? rawField, ...(nextSorter?.order && { order: nextSorter.order as 'ascend' | 'descend' }) }
                : { field: cancelledField };

            setPageQuery({
                ...pageState,
                pagination: { current: tablePagination.current ?? 1, pageSize: tablePagination.pageSize ?? DEFAULT_PAGINATION.pageSize },
                sorter: newSorter,
            });
        },
        [pageState, sorter, setPageQuery]
    );

    return { filters, search, pagination, sorter, query, setFilters, clearFilters, setSearch, onTableChange };
};
